import { useEffect, useState } from "react";
import { Trans, Plural, useLingui } from "@lingui/react/macro";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Form from "@/components/Form/Form";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import { Text } from "@medoma/components/Text/Text";
import { activityKeys, addActivitiesFromGroupTemplate } from "@/api/Activities";
import {
  type INewActivityWithOptionalInvoicingCodes,
  saveMultipleActivitiesInvoicingCodes,
} from "@/api/Invoicing";
import type { IBatchTemplate } from "@/api/Templates";
import { machineFormat } from "@medoma/models/date-and-time";
import { deducedError } from "@/Utils/ErrorUtils";
import { useTemplatesContext } from "../TemplatesContext";
import type { CreateActivitiesFromBatchTemplateFormFields } from "./createActivitiesFromBatchTemplateFormSchema";
import {
  batchTemplateActivityInclusionTypeSchema,
  batchTemplateActivityDelayedStartDateUnitSchema,
  batchTemplateActivityTimeConfigTypeSchema,
  type IBatchTemplateActivity,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { newActivityFromBatchTemplateActivity } from "./newActivityFromBatchTemplateActivity";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import styles from "./CreateActivitiesFromBatchTemplateForm.module.scss";
import HiddenIcon from "@medoma/components/icons/HiddenIcon";
import InvoicingBillIcon from "@medoma/components/icons/InvoicingBillIcon";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import PenIcon from "@medoma/components/icons/PenIcon";
import { DateInput } from "@/forms/AddActivityForm/Scheduling/DateInput";
import { TemplateDateInput } from "@/pages/template-manager/TemplateDateInput";
import { TemplateScheduling } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateScheduling/TemplateScheduling";
import { activityFrequencySchema } from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import { FormFieldPathPrefixContextProvider } from "@/forms/FormFieldPathPrefixContextProvider";
import { TemplateFieldValidationContextProvider } from "@/pages/template-manager/TemplateFieldValidationContextProvider";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import MeasurementChip from "@/components/Chips/MeasurementChip";
import { activityRequirementSchema } from "@medoma/models/activityRequirements";
import NoResults from "@/components/NoResults/NoResults";
import { weekdayDictionary } from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import ClockIcon from "@medoma/components/icons/ClockIcon";
import { TemplateDescriptionInput } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateDescriptionInput";
import { resolveActivityStartDate } from "./resolveActivityStartDate";
import { TemplateTimeSlotsPicker } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateScheduling/TemplateTimeSlotsPicker";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import { getRecurrenceLabel } from "../templateUtils";
import { routeKeys } from "@/api/Routes";
import { shiftKeys } from "@/api/Shifts";

type CreateActivitiesFromBatchTemplateFormProps = {
  template: IBatchTemplate;
  onSuccess?: () => void;
};

export const CreateActivitiesFromBatchTemplateForm = ({
  template,
  onSuccess,
}: CreateActivitiesFromBatchTemplateFormProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { selectedPatientId: patientId, defaultStartDate } =
    useTemplatesContext();

  const activities = template.template.activities;

  // Build default values for the form based on inclusionType
  const activityDefaults = Object.fromEntries(
    activities.map((activity) => {
      const isDefaultSelected =
        activity.inclusionType ===
        batchTemplateActivityInclusionTypeSchema.enum
          .optionalWithDefaultSelected;

      return [
        activity.id,
        {
          selected: isDefaultSelected,
          description: activity.description,
          timeCategory: activity.timeCategory,
          recurrencesPerDay: activity.recurrencesPerDay,
          timeslots: activity.timeslots,
          timeSensitivity: activity.timeSensitivity,
        },
      ];
    }),
  );

  const methods = useForm<CreateActivitiesFromBatchTemplateFormFields>({
    defaultValues: {
      startDate: defaultStartDate
        ? machineFormat(defaultStartDate, "yyyy-MM-dd")
        : undefined,
      selectedActivities: activityDefaults,
      sharedTimeConfig: template.template.sharedTimeConfig,
    },
  });

  const {
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    reset,
    getValues,
    watch,
  } = methods;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const sharedFrequency = watch("sharedTimeConfig.frequency");
  const hasSharedTimeConfigActivities =
    activities.filter((a) => a.timeConfigType === "shared").length > 0;
  const hasRecurringSharedTimeConfig =
    hasSharedTimeConfigActivities &&
    sharedFrequency === activityFrequencySchema.enum.recurring;

  const showBatchEndDateInput = hasRecurringSharedTimeConfig;

  const { mutate, isPending } = useMutation({
    mutationFn: async (
      formData: CreateActivitiesFromBatchTemplateFormFields,
    ) => {
      const selectedActivities = activities.filter(
        (activity) => formData.selectedActivities[activity.id]?.selected,
      );

      const batchStartDate = formData.startDate;
      const batchEndDate = formData.endDate;

      const newActivities: INewActivityWithOptionalInvoicingCodes[] =
        selectedActivities.map((activity) => {
          const selectedActivityFormData =
            formData.selectedActivities[activity.id];

          const usesBatchConfig =
            activity.timeConfigType ===
            batchTemplateActivityTimeConfigTypeSchema.enum.shared;

          const activityHasOwnStartDate = selectedActivityFormData?.startDate;

          // Shared activity uses batchStartDate
          // Individual uses startDate if it exists and has a value
          // if not, resolveStartDate by delayedStartDate config relative batchStartDate
          const startDate = usesBatchConfig
            ? batchStartDate
            : activityHasOwnStartDate
              ? (selectedActivityFormData.startDate as string) // Make this better. We know we have a date string at this point
              : resolveActivityStartDate(activity, batchStartDate);

          // Shared activity uses batchEndDate if batchSchedule is recurring
          // Individual uses its own endDate if it has a value
          const endDate = usesBatchConfig
            ? batchEndDate
            : selectedActivityFormData?.endDate;

          const activitySchedule = usesBatchConfig
            ? formData.sharedTimeConfig
            : {
                timeCategory: selectedActivityFormData?.timeCategory,
                recurrencesPerDay: selectedActivityFormData?.recurrencesPerDay,
                timeslots: selectedActivityFormData?.timeslots,
                timeSensitivity: selectedActivityFormData?.timeSensitivity,
              };

          return {
            ...newActivityFromBatchTemplateActivity({
              activity: {
                ...activity,
                ...activitySchedule,
                description: selectedActivityFormData?.description,
              },
              startDate,
              endDate,
              patientId,
              templateId: template.templateId,
              templateRevision: template.revisionNumber,
            }),
          };
        });

      await addActivitiesFromGroupTemplate({
        templateId: template.templateId,
        templateRevision: template.revisionNumber,
        newActivities,
      });
      const activitiesWithInvoicingCodes = newActivities
        .filter(
          (activity) =>
            activity.patientId &&
            ((activity.procedureCodes && activity.procedureCodes.length > 0) ||
              (activity.productCodes && activity.productCodes.length > 0)),
        )
        .map((activity) => ({
          activityId: activity.activityId,
          patientId: activity.patientId as string,
          procedureCodes: activity.procedureCodes || [],
          productCodes: activity.productCodes || [],
        }));
      if (activitiesWithInvoicingCodes.length > 0) {
        await saveMultipleActivitiesInvoicingCodes(
          activitiesWithInvoicingCodes,
        );
      }
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityKeys.all }),
        queryClient.invalidateQueries({ queryKey: routeKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: shiftKeys.listWithContents({}),
        }),
      ]);
      onSuccess?.();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const anySelected = activities.some(
      (a) => formData.selectedActivities[a.id]?.selected,
    );
    if (!anySelected) {
      setError("root.noSelectedActivities", {
        message: t`Välj minst en aktivitet`,
      });
      return;
    }
    mutate(formData);
  });

  if (activities.length === 0) {
    return (
      <NoResults
        message={t`Inga aktiviteter hittades. Kontrollera att du har valt rätt mall och att mallen innehåller aktiviteter.`}
      />
    );
  }

  const activitiesWithSharedTimeConfig = activities.filter(
    (activity) => activity.timeConfigType === "shared",
  );
  const activitiesWithIndividualTimeConfig = activities.filter(
    (activity) => activity.timeConfigType === "individual",
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {/* ACTIVITIES WITH SHARED TIME CONFIG */}
        {activitiesWithSharedTimeConfig.length > 0 ? (
          <section className={styles.activitiesWithSharedTimeConfig}>
            <Checkboxes width="fill-container">
              {activitiesWithSharedTimeConfig.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </Checkboxes>
            <FormFieldPathPrefixContextProvider prefix="sharedTimeConfig">
              <TemplateFieldValidationContextProvider
                value={{ isInFinalValidationPhase: true }}
              >
                <div className={styles.sharedTimeConfigFields}>
                  <TemplateScheduling />
                </div>
              </TemplateFieldValidationContextProvider>
            </FormFieldPathPrefixContextProvider>
          </section>
        ) : null}

        {/* START DATE (with optional end date when recurring shared time config, and quick selects) */}
        <DateInput hasEndDate={showBatchEndDateInput} />

        {activitiesWithSharedTimeConfig.length > 0 ? (
          <div className={styles.divider} />
        ) : null}

        {/* ACTIVITIES WITH INDIVIDUAL TIME CONFIG */}
        {activitiesWithIndividualTimeConfig.length > 0 ? (
          <section className={styles.activitiesWithIndividualTimeConfig}>
            <Checkboxes width="fill-container">
              {activitiesWithIndividualTimeConfig.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </Checkboxes>
          </section>
        ) : null}

        {errors.root?.noSelectedActivities?.message ? (
          <ErrorMessage
            message={errors.root.noSelectedActivities.message}
            weight="bold"
          />
        ) : null}

        {errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} weight="bold" />
        ) : null}

        <FilledButton type="submit" disabled={isPending}>
          <Trans>Lägg till aktiviteter</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};

const ActivityRow = ({ activity }: { activity: IBatchTemplateActivity }) => {
  const { t } = useLingui();
  const { clearErrors, register, watch, resetField } =
    useFormContext<CreateActivitiesFromBatchTemplateFormFields>();

  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const [showTimeInput, setShowTimeInput] = useState(false);
  const [showDateInput, setShowDateInput] = useState(false);

  const activityIsSelected = watch(
    `selectedActivities.${activity.id}.selected`,
  );

  useEffect(() => {
    if (!activityIsSelected) {
      resetField(`selectedActivities.${activity.id}.description`);
      resetField(`selectedActivities.${activity.id}.startDate`);
      resetField(`selectedActivities.${activity.id}.endDate`);
      resetField(`selectedActivities.${activity.id}.timeCategory`);
      resetField(`selectedActivities.${activity.id}.recurrencesPerDay`);
      resetField(`selectedActivities.${activity.id}.timeslots`);
      resetField(`selectedActivities.${activity.id}.timeSensitivity`);
      // oxlint-disable-next-line react/set-state-in-effect
      setShowDescriptionInput(false);
      setShowTimeInput(false);
      setShowDateInput(false);
    }
  }, [activity.id, activityIsSelected, resetField]);

  const hasInvoicingCodes =
    (activity.procedureCodes && activity.procedureCodes.length > 0) ||
    (activity.productCodes && activity.productCodes.length > 0);
  const isHidden = activity.hidden;

  const descriptionFromTemplate = activity.description;
  const hasDescriptionFromTemplate = Boolean(descriptionFromTemplate);
  const hasDoubleStaffing = activity.doubleStaffing;
  const hasIndividualTimeConfig = activity.timeConfigType === "individual";

  const canEditTime = hasIndividualTimeConfig;
  const isRecurring =
    activity.frequency === activityFrequencySchema.enum.recurring;

  const weekdayOffset = activity.delayedStartDateWeekday
    ? t(
        weekdayDictionary[activity.delayedStartDateWeekday].regular,
      ).toLocaleLowerCase()
    : null;
  const weekdayOffsetLabel = weekdayOffset
    ? t`Nästkommande ${weekdayOffset} efter valt startdatum`
    : null;

  const isAnyTimeOfDay = activity.timeCategory === timeOfDaySchema.enum.Any;
  const timeLabel = isAnyTimeOfDay
    ? t(timeOfDayDictionary.Any.short)
    : activity.timeslots && activity.timeslots.length > 0
      ? activity.timeslots.map((slot) => slot.time).join(", ")
      : undefined;

  const recurrenceLabelText = getRecurrenceLabel({
    recurrence: activity.recurrence,
    customRecurrenceInterval: activity.customRecurrenceInterval,
    customRecurrenceIntervalUnit: activity.customRecurrenceIntervalUnit,
    weekdays: activity.weekdays,
  });

  return (
    <div className={styles.activityWrapper}>
      <div className={styles.activityCheckboxWrapper}>
        <Checkbox
          label={{
            text: activity.title,
            component: <ActivityCheckboxDisplayLabel activity={activity} />,
          }}
          {...register(`selectedActivities.${activity.id}.selected`, {
            onChange: () => clearErrors("root.noSelectedActivities"),
          })}
        />
        {hasInvoicingCodes ? <InvoicingBillIcon /> : null}
        {isHidden ? <HiddenIcon /> : null}
        {activity.requiredCompetences &&
        activity.requiredCompetences.length > 0 ? (
          <div className={styles.competences}>
            {activity.requiredCompetences.map((competence) => (
              <CompetenceChip
                key={competence}
                competence={competence}
                state="neutral"
                size="tiny"
              />
            ))}
            {hasDoubleStaffing ? (
              <RequirementChip
                requirement={activityRequirementSchema.enum.DoubleStaffing}
                state="neutral"
                size="tiny"
              />
            ) : null}
          </div>
        ) : null}
        {/* Special cases where title needs extra description */}
        {activity.measurements && activity.measurements.length > 0 ? (
          <div className={styles.measurements}>
            {activity.measurements.map((measurement) => (
              <MeasurementChip
                key={measurement}
                measurement={measurement}
                size="tiny"
              />
            ))}
          </div>
        ) : null}
        {activityIsSelected ? (
          <div className={styles.activityEditButtons}>
            {!showDescriptionInput ? (
              <PlainButton
                type="button"
                size="small"
                weight="light"
                onClick={() => setShowDescriptionInput(true)}
              >
                {hasDescriptionFromTemplate ? (
                  <>
                    <PenIcon />
                    <Trans>Ändra beskrivning</Trans>
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    <Trans>Beskrivning</Trans>
                  </>
                )}
              </PlainButton>
            ) : null}
            {canEditTime && !showTimeInput ? (
              <PlainButton
                type="button"
                size="small"
                weight="light"
                onClick={() => {
                  setShowTimeInput(true);
                  setShowDateInput(true);
                }}
              >
                <ClockIcon />
                <Trans>Ändra tid</Trans>
              </PlainButton>
            ) : null}
          </div>
        ) : null}
      </div>
      {hasIndividualTimeConfig ? (
        <div className={styles.startDateOffsetAndSchedule}>
          {/* If date input is displayed, we better hide the offset info to avoid possible confusing mismatches */}
          {showDateInput ? null : (
            <>
              {/* No offset */}
              {activity.delayedStartDate === "none" ? (
                <Text element="p" size="small">
                  <Trans>Samtidigt som valt startdatum</Trans>
                </Text>
              ) : null}

              {/* Offset by days or weeks */}
              {activity.delayedStartDate === "offset" &&
              activity.delayedStartDateOffset &&
              activity.delayedStartDateUnit ? (
                <Text element="p" size="small">
                  {activity.delayedStartDateUnit ===
                  batchTemplateActivityDelayedStartDateUnitSchema.enum.week ? (
                    <Plural
                      value={activity.delayedStartDateOffset}
                      _1="1 vecka efter valt startdatum"
                      other="# veckor efter valt startdatum"
                    />
                  ) : (
                    <Plural
                      value={activity.delayedStartDateOffset}
                      _1="1 dag efter valt startdatum"
                      other="# dagar efter valt startdatum"
                    />
                  )}
                </Text>
              ) : null}

              {/* Offset by next weekday */}
              {activity.delayedStartDate === "weekday" &&
              activity.delayedStartDateWeekday &&
              weekdayOffsetLabel ? (
                <Text element="p" size="small">
                  {weekdayOffsetLabel}
                </Text>
              ) : null}

              {/* User-selected start date label */}
              {activity.delayedStartDate === "userSelects" ? (
                <Text element="p" size="small">
                  {isRecurring ? (
                    <Trans>Välj startdatum</Trans>
                  ) : (
                    <Trans>Välj datum</Trans>
                  )}
                </Text>
              ) : null}
            </>
          )}

          <div className={styles.schedule}>
            {isRecurring && recurrenceLabelText ? (
              <div className={styles.recurrenceLabel}>
                <RecurrenceIcon />
                <Text element="span" size="small" color="faded">
                  {recurrenceLabelText}
                </Text>
              </div>
            ) : null}
            {/* If time input is displayed, we hide the time info to avoid having to sync values */}
            {!showTimeInput ? (
              <Text element="span" size="small" color="faded">
                {timeLabel}
              </Text>
            ) : null}
          </div>
        </div>
      ) : null}
      {hasDescriptionFromTemplate && !showDescriptionInput ? (
        <div className={styles.templateDescription}>
          <Text element="p" color="faded" size="small">
            {descriptionFromTemplate}
          </Text>
        </div>
      ) : null}
      {showDescriptionInput || showTimeInput || showDateInput ? (
        <div className={styles.activityEditFields}>
          <FormFieldPathPrefixContextProvider
            prefix={`selectedActivities.${activity.id}`}
          >
            {showDescriptionInput ? <TemplateDescriptionInput /> : null}
            {showTimeInput ? (
              <TemplateFieldValidationContextProvider
                value={{ isInFinalValidationPhase: true }}
              >
                <TemplateTimeSlotsPicker
                  isRecurring={isRecurring}
                  size="compact"
                />
              </TemplateFieldValidationContextProvider>
            ) : null}
            {showDateInput ? (
              <TemplateDateInput size="compact" hasEndDate={isRecurring} />
            ) : null}
          </FormFieldPathPrefixContextProvider>
        </div>
      ) : null}
    </div>
  );
};

const ActivityCheckboxDisplayLabel = ({
  activity,
}: {
  activity: IBatchTemplateActivity;
}) => {
  return (
    <div className={styles.activityCheckboxLabel}>
      <CategoryIcon size="tiny" category={activity.category} />
      <Text element="span">{activity.title}</Text>
    </div>
  );
};
